import React, { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Col, Dropdown, Icon, Input, Menu, Row, Table } from "antd";
import { Creators as MeasurementActions } from "../../store/ducks/measurement";
import { CardCustom, Title } from "./styles";

import DrawerMeasurement from "../../components/drawers/measurement";

import TagStatus from "../../components/utils/tagStatus";
import { Container as MenuContainer } from "../../components/utils/table/menu/styles";
import MenuIcon from "../../components/utils/table/icons/menu";
import ButtonStandard from "../../components/utils/button";

const Measurement = () => {
  const dispatch = useDispatch();
  const {
    measurement: { data, isLoading },
    app: {
      translation,
      groupSelected: { id: groupId },
      farmSelected: { id: farmId },
    },
  } = useSelector((state) => state);

  useEffect(() => {
    if (groupId && farmId) {
      dispatch(MeasurementActions.index(groupId, farmId));
    }
  }, [groupId, farmId]);

  const { Column } = Table;

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleGetColumnSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon="search"
            size="small"
            style={{
              width: 90,
              marginRight: 8,
              background: "#684e94",
              borderColor: "none",
              border: "none",
            }}
          />
          <Button
            onClick={() => handleReset(clearFilters)}
            icon="delete"
            size="small"
            style={{ width: 90 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
    }),
    [handleReset, handleSearch]
  );

  function handleNewMeasurement() {
    dispatch(MeasurementActions.showOrHideDrawer());
  }

  function handleInactivateOrActivate(record, action) {
    try {
      if (action === "inactivate") {
        dispatch(
          MeasurementActions.inactivateMeasurement(groupId, farmId, record.id)
        );
      } else {
        dispatch(
          MeasurementActions.activateMeasurement(groupId, farmId, record.id)
        );
      }
    } catch (error) {}
  }

  function handleEdit(record) {
    dispatch(MeasurementActions.showOrHideDrawer(record.id));
  }

  // Default global: groupId === null (não edita, só ativa/inativa)
  // Custom da farm: groupId !== null (edita + ativa/inativa)
  function isDefault(record) {
    return record.groupId === null;
  }

  function renderMenu(record) {
    return (
      <Menu>
        {!isDefault(record) && record.status === "Active" && (
          <Menu.Item key="0">
            <button onClick={() => handleEdit(record)}>
              {translation.table.menu.edit}
            </button>
          </Menu.Item>
        )}
        {record.status !== "Active" ? (
          <Menu.Item key="1">
            <button
              onClick={() => handleInactivateOrActivate(record, "activate")}
            >
              {translation.table.menu.activate}
            </button>
          </Menu.Item>
        ) : (
          <Menu.Item key="2">
            <button
              onClick={() => handleInactivateOrActivate(record, "inactivate")}
            >
              {translation.table.menu.inactivate}
            </button>
          </Menu.Item>
        )}
      </Menu>
    );
  }

  return (
    <CardCustom>
      <Row type="flex" justify="space-between" style={{ marginBottom: "19px" }}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Title>{translation.measurement.title}</Title>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12} align="right">
          <ButtonStandard buttonType="addNew" onClick={handleNewMeasurement}>
            {translation.measurement.buttonNew}
          </ButtonStandard>
        </Col>
      </Row>
      <Row type="flex">
        <Table
          loading={isLoading}
          rowKey="id"
          size="small"
          dataSource={data !== null ? data : []}
          scroll={{ x: true }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
          }}
          style={{ width: "100%", margin: "0" }}
        >
          <Column
            title={translation.measurement.table.columns.name}
            dataIndex="name"
            key="name"
            align="left"
            sorter={(first, second) => {
              return first.name.localeCompare(second.name, "pt-BR", {
                numeric: false,
                ignorePunctuation: true,
              });
            }}
            {...handleGetColumnSearchProps("name")}
          />
          <Column
            title={translation.measurement.table.columns.code}
            dataIndex="code"
            key="code"
            align="left"
            sorter={(first, second) => {
              return first.code.localeCompare(second.code, "pt-BR", {
                numeric: false,
                ignorePunctuation: true,
              });
            }}
          />
          <Column
            title={translation.measurement.table.columns.status}
            dataIndex="status"
            key="status"
            align="left"
            defaultFilteredValue={["Active"]}
            filtered
            filters={[
              {
                text: translation.status.active,
                value: "Active",
              },
              {
                text: translation.status.inactive,
                value: "Inactive",
              },
            ]}
            filterMultiple
            onFilter={(value, record) => record.status.includes(value)}
            render={(status) =>
              status === "Active" ? (
                <TagStatus
                  background="#C5F1CA"
                  borderColor="#106518"
                  color="#106518"
                >
                  {translation.status.active}
                </TagStatus>
              ) : (
                <TagStatus
                  background="#FBC7C7"
                  borderColor="#D44C4C"
                  color="#D44C4C"
                >
                  {translation.status.inactive}
                </TagStatus>
              )
            }
          />
          <Column
            align="left"
            width={50}
            render={(text, record) => (
              <Dropdown
                overlay={renderMenu(record)}
                trigger={["click"]}
                key={record.id}
              >
                <MenuContainer>
                  <MenuIcon />
                </MenuContainer>
              </Dropdown>
            )}
          />
        </Table>
      </Row>
      <DrawerMeasurement />
    </CardCustom>
  );
};

export default Measurement;
